import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Region, Seal, Signature } from '@paragon/contract-library';

interface SealsAndSignaturesState {
  readonly regions: readonly Region[];
  readonly seals: readonly Seal[];
  readonly signatures: readonly Signature[];
  readonly selectedRegion: Region | null;
}

const initialState: SealsAndSignaturesState = {
  regions: [],
  seals: [],
  signatures: [],
  selectedRegion: null,
};

const sealsAndSignaturesSlice = createSlice({
  name: 'sealsAndSignatures',
  initialState,
  reducers: {
    setSealsAndSignaturesData: (
      state,
      action: PayloadAction<{
        readonly regions: readonly Region[];
        readonly seals: readonly Seal[];
        readonly signatures: readonly Signature[];
      }>,
    ) => {
      state.regions = castDraft(action.payload.regions);
      state.seals = castDraft(action.payload.seals);
      state.signatures = castDraft(action.payload.signatures);
    },
    selectRegion: (state, action: PayloadAction<Region>) => {
      state.selectedRegion = action.payload;
    },
    removeSeal: (state, action: PayloadAction<number>) => {
      state.seals = state.seals.filter((seal) => seal.id !== action.payload);
    },
    updateSeal: (state, action: PayloadAction<Seal>) => {
      const updatedSeal = action.payload;
      const sealIndexToUpdate = state.seals.findIndex((seal) => seal.id === updatedSeal.id);
      state.seals[sealIndexToUpdate] = updatedSeal;
    },
    createSeal: (state, action: PayloadAction<Seal>) => {
      state.seals.push(action.payload);
    },
    createSignatures: (state, action: PayloadAction<readonly Signature[]>) => {
      state.signatures.push(...action.payload);
    },
    removeSignature: (state, action: PayloadAction<number>) => {
      state.signatures = state.signatures.filter((signature) => signature.id !== action.payload);
    },
  },
});

export const {
  setSealsAndSignaturesData,
  selectRegion,
  removeSeal,
  updateSeal,
  createSeal,
  createSignatures,
  removeSignature,
} = sealsAndSignaturesSlice.actions;
export const sealsAndSignatures = sealsAndSignaturesSlice.reducer;
